import { ReactComponent as InfoIcon } from "../../shared/assets/informationLogo.svg";
import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import hullAreasGuide from "../assets/hull-areas-guide.png";
import { AntifoulingCoatingField } from "../components/AntifoulingCoatingField";
import { TechFormDateInput } from "../components/TechFormDateInput";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import { INITIAL_FORM_DATA, TYPE_OF_ANTIFOULING_OPTIONS } from "../models/constants";
import type { TechFormAntifoulingPageProps } from "../models/tech-form-page.models";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import {
  BOOT_TOP_GUIDANCE,
  NO_ANTIFOULING_BOOT_TOP_OPTION,
  OTHER_ANTIFOULING_MANUFACTURER_VALUE,
  SERVICE_LIFE_GUIDANCE,
} from "../models/tq4-antifouling.constants";
import {
  Tq4AntifoulingCoatingField as Tq4AntifoulingCoatingFieldName,
  Tq4AntifoulingM,
  mapSqlToTq4AntifoulingForm,
} from "../models/tq4-antifouling.models";
import { OptionType, TechFormPaintOptionsByVendor } from "../models/types";
import { isTechFormSectionDataCached, loadTechFormEndpointData } from "../services/tech-form-section-data.service";
import { getTechFormPaintOptions, getTechFormPortOptions } from "../services/tech-forms.service";
import { tq4AntiFoulingDetailsAtom } from "../state/tech-form.atoms";
import { ReactSelectStyling } from "../utils/TechUtils";
import { sanitizeTechFormInputForField, validateTq4Antifouling } from "../utils/tech-form-validations";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

export const Tq4Antifouling: React.FC<TechFormAntifoulingPageProps> = ({
  isCompletedForm,
  cacheInitialForm,
  onSectionReady,
}) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(tq4AntiFoulingDetailsAtom);

  const { imoNumber, vesselName } = useParams();
  const [form, setForm] = useState<Tq4AntifoulingM>(() => {
    return Object.keys(storedForm ?? {}).length > 0 ? storedForm : INITIAL_FORM_DATA;
  });

  const [formErrors, setFormErrors] = useState<Partial<Record<keyof Tq4AntifoulingM, string>>>({});
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isSectionDataReady, setIsSectionDataReady] = useState(false);
  const [sectionLoadError, setSectionLoadError] = useState(false);
  const [sectionLoadAttempt, setSectionLoadAttempt] = useState(0);
  const [manufacturerOptions, setManufacturerOptions] = useState<OptionType[]>([]);
  const [paintOptionsByVendor, setPaintOptionsByVendor] = useState<TechFormPaintOptionsByVendor>({});
  const [paintOptionsLoaded, setPaintOptionsLoaded] = useState(false);
  const [isLoadingPaintOptions, setIsLoadingPaintOptions] = useState(false);
  const [portOptions, setPortOptions] = useState<OptionType[]>([]);
  const [portOptionsLoaded, setPortOptionsLoaded] = useState(false);
  const [isLoadingPortOptions, setIsLoadingPortOptions] = useState(false);

  useEffect(() => {
    let ignoreResult = false;
    setSectionLoadError(false);
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;
    if (isCompletedForm) {
      void fetchData(() => ignoreResult);
    } else {
      if (storageisEmpty) {
        void fetchData(() => ignoreResult);
      } else {
        setForm(_form);
        cacheInitialForm(_form);
        setIsSectionDataReady(true);
        onSectionReady?.();
      }
    }

    return () => {
      ignoreResult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm, imoNumber, sectionLoadAttempt, vesselName]);

  useEffect(() => {
    setFormErrors(validateTq4Antifouling(form));
  }, [form]);

  const fetchData = async (isStale: () => boolean) => {
    if (!imoNumber || !vesselName) return;

    try {
      const requestIdentity = {
        imoNumber,
        vesselName,
        mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
      };
      if (!isTechFormSectionDataCached({ ...requestIdentity, section: 4 })) {
        setLoading(true);
      }
      const result = await loadTechFormEndpointData<any[]>({
        ...requestIdentity,
        endpoint: TECH_FORM_ENDPOINTS.antifoulingDetails,
      });
      if (isStale()) return;

      const _antifouling = result[0];
      const antifouling = _antifouling ? mapSqlToTq4AntifoulingForm(_antifouling) : { ...INITIAL_FORM_DATA };
      setForm(antifouling);
      setFormErrors(validateTq4Antifouling(antifouling));
      setStoredForm(antifouling);
      cacheInitialForm(antifouling);
      setIsSectionDataReady(true);
      onSectionReady?.();
    } catch (error: unknown) {
      if (!isStale()) {
        setSectionLoadError(true);
        console.error("Unable to load Technical Form antifouling data", error);
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const updatedValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : sanitizeTechFormInputForField(name, value);

    if (type === "checkbox") {
      setForm((prevState) => {
        const nextForm = {
          ...prevState,
          [name]: updatedValue,
        };
        setFormErrors(validateTq4Antifouling(nextForm));
        return nextForm;
      });
    } else {
      setForm((prevState) => {
        const nextForm = {
          ...prevState,
          [name]: updatedValue,
        };
        setFormErrors(validateTq4Antifouling(nextForm));
        return nextForm;
      });
    }

    storageSetItem({ [name]: updatedValue });
  };

  const handleChangeType = (newValue: OptionType | null) => {
    setForm((prevData) => ({
      ...prevData,
      typeOfAntiFoulingSystem: newValue,
    }));
    storageSetItem({ typeOfAntiFoulingSystem: newValue });
  };

  const handleChangeManufacturer = (newValue: OptionType | null) => {
    setForm((prevData) => ({
      ...prevData,
      antiFoulingSystemManufacturer: newValue,
    }));
    storageSetItem({ antiFoulingSystemManufacturer: newValue });
  };

  // Storage
  useEffect(() => {
    if (isSectionDataReady) {
      setStoredForm(form);
    }
  }, [form, isSectionDataReady, setStoredForm]);

  const storageSetItem = (obj: any) => {
    setStoredForm({
      ...form,
      ...obj,
    });
  };

  const selectStyles = ReactSelectStyling();

  const extraStyles = {
    ...selectStyles,
    control: (base: any, state: any) => ({
      ...selectStyles.control(base, state),
      height: 48,
    }),
  };

  const loadPaintOptions = async () => {
    if (paintOptionsLoaded || isLoadingPaintOptions) {
      return;
    }

    setIsLoadingPaintOptions(true);
    try {
      const options = await getTechFormPaintOptions();
      setManufacturerOptions(options.manufacturerOptions);
      setPaintOptionsByVendor(options.paintOptionsByVendor);
      setPaintOptionsLoaded(true);
    } catch (error) {
      console.error("Error loading tech form paint options", error);
    } finally {
      setIsLoadingPaintOptions(false);
    }
  };

  const loadPortOptions = async () => {
    if (portOptionsLoaded || isLoadingPortOptions) {
      return;
    }

    setIsLoadingPortOptions(true);
    try {
      setPortOptions(await getTechFormPortOptions());
      setPortOptionsLoaded(true);
    } catch (error) {
      console.error("Error loading tech form port options", error);
    } finally {
      setIsLoadingPortOptions(false);
    }
  };

  const selectedManufacturerKey = (() => {
    const selected = form.antiFoulingSystemManufacturer;
    if (!selected?.value) {
      return "";
    }

    if (paintOptionsByVendor[selected.value]) {
      return selected.value;
    }

    const matchingVendor = manufacturerOptions.find(
      (option) => option.label === selected.label || option.value === selected.label,
    );

    return matchingVendor?.value ?? selected.value;
  })();

  const antiFoulingCoatingOptions = selectedManufacturerKey ? paintOptionsByVendor[selectedManufacturerKey] ?? [] : [];
  const bootTopCoatingOptions = antiFoulingCoatingOptions.some(
    (option) => option.value === NO_ANTIFOULING_BOOT_TOP_OPTION.value,
  )
    ? antiFoulingCoatingOptions
    : [...antiFoulingCoatingOptions, NO_ANTIFOULING_BOOT_TOP_OPTION];
  const allowsCustomCoatingEntry = form.antiFoulingSystemManufacturer?.value === OTHER_ANTIFOULING_MANUFACTURER_VALUE;

  const manufacturerValue =
    manufacturerOptions.find(
      (option) =>
        option.value === form.antiFoulingSystemManufacturer?.value ||
        option.label === form.antiFoulingSystemManufacturer?.label,
    ) ??
    form.antiFoulingSystemManufacturer ??
    null;

  const optionForPort = (value: string) =>
    portOptions.find((option) => option.value === value || option.label === value) ??
    (value ? { value, label: value } : null);

  const handlePortSelectChange = (
    field: "underWaterHullCleaningPortNameOne" | "underWaterHullCleaningPortNameTwo",
    newValue: OptionType | null,
  ) => {
    const value = newValue?.label ?? "";
    setForm((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    storageSetItem({ [field]: value });
  };

  const handleCoatingValueChange = (field: Tq4AntifoulingCoatingFieldName, rawValue: string) => {
    const value = sanitizeTechFormInputForField(field, rawValue);
    setForm((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    storageSetItem({ [field]: value });
  };

  const renderYesNo = (name: keyof Tq4AntifoulingM) => (
    <div className="mt-1 whitespace-nowrap">
      <label className="inline-flex cursor-pointer items-center">
        <input
          type="radio"
          className="form-radio"
          name={name}
          value="yes"
          checked={form[name] === "yes"}
          onChange={handleInputChange}
        />
        <span className="ml-2">Yes</span>
      </label>
      <label className="ml-4 inline-flex cursor-pointer items-center">
        <input
          type="radio"
          className="form-radio"
          name={name}
          value="no"
          checked={form[name] === "no"}
          onChange={handleInputChange}
        />
        <span className="ml-2">No</span>
      </label>
      {formErrors[name] && <p className="mt-1 text-xs text-red-600">{formErrors[name]}</p>}
    </div>
  );

  if (sectionLoadError) {
    return <TechFormSectionLoadError onRetry={() => setSectionLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
        )}
      >
        <h1 className="custom-heading mb-6">Antifouling Systems & UW Ops</h1>
        <div className="mt-4 flex max-w-[280px] flex-col justify-start gap-1">
          <label className="text-sm font-bold">Under-water hull inspection after last dry dock?</label>
          {renderYesNo("hasUnderwaterHullInspectionAfterLastDrydock")}
        </div>

        {form.hasUnderwaterHullInspectionAfterLastDrydock === "yes" && (
          <>
            <label className="mt-6 block">
              Please indicate the last two (as applicable) most recent under-water hull cleaning after last dry dock
              (including propeller polishing):
            </label>

            <table className="w-50 ml-[-32px] mt-2 border-separate border-spacing-x-[32px] border-spacing-y-[16px] text-left text-sm">
              <thead className="text-sm">
                <tr>
                  <th scope="col">Under-water hull cleaning</th>
                  <th scope="col">Port name</th>
                  <th scope="col">Date</th>
                  <th scope="col">Hull Inspected?</th>
                  <th scope="col">Hull Cleaned?</th>
                  <th scope="col">Propeller Polished</th>
                  <th scope="col">Was this cleaning as per NZ MPI standard cleaning?</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td className="w-[100px]">
                    <div
                      className={twMerge(
                        "rounded-md border p-3",
                        theme.uwhcAssistant.inputBorder,
                        theme.uwhcAssistant.tableBg,
                        theme.vesselDetails.info,
                      )}
                    >
                      1
                    </div>
                  </td>
                  <td>
                    <Select
                      className="min-w-[220px]"
                      classNamePrefix="rs"
                      data-test="tq4-under-water-hull-cleaning-port-one-select"
                      inputId="underWaterHullCleaningPortNameOne"
                      name="underWaterHullCleaningPortNameOne"
                      options={portOptions}
                      value={optionForPort(form.underWaterHullCleaningPortNameOne)}
                      onChange={(newValue) => handlePortSelectChange("underWaterHullCleaningPortNameOne", newValue)}
                      onMenuOpen={loadPortOptions}
                      isLoading={isLoadingPortOptions}
                      isSearchable
                      placeholder="Port name..."
                      styles={extraStyles}
                    />
                  </td>
                  <td>
                    <TechFormDateInput
                      name="underWateHullCleaningDateOne"
                      value={form.underWateHullCleaningDateOne}
                      error={formErrors.underWateHullCleaningDateOne}
                      onChange={(value) => setForm((current) => ({ ...current, underWateHullCleaningDateOne: value }))}
                      className={twMerge("rounded-md focus:outline-none", theme.uwhcAssistant.border)}
                    />
                  </td>
                  <td>{renderYesNo("underWaterHullCleaningHullInspectedOne")}</td>
                  <td>{renderYesNo("underWaterHullCleaningHullCleanedOne")}</td>
                  <td>{renderYesNo("underWateHullCleaningPropellerPolishedOne")}</td>
                  <td>{renderYesNo("underWaterHullCleaningNzMpiStandardCleaningOne")}</td>
                </tr>

                <tr>
                  <td className="whitespace-nowrap font-medium">
                    <div
                      className={twMerge(
                        "rounded-md border p-3",
                        theme.uwhcAssistant.inputBorder,
                        theme.uwhcAssistant.tableBg,
                        theme.vesselDetails.info,
                      )}
                    >
                      2
                    </div>
                  </td>
                  <td>
                    <Select
                      className="min-w-[220px]"
                      classNamePrefix="rs"
                      data-test="tq4-under-water-hull-cleaning-port-two-select"
                      inputId="underWaterHullCleaningPortNameTwo"
                      name="underWaterHullCleaningPortNameTwo"
                      options={portOptions}
                      value={optionForPort(form.underWaterHullCleaningPortNameTwo)}
                      onChange={(newValue) => handlePortSelectChange("underWaterHullCleaningPortNameTwo", newValue)}
                      onMenuOpen={loadPortOptions}
                      isLoading={isLoadingPortOptions}
                      isSearchable
                      placeholder="Port name..."
                      styles={extraStyles}
                    />
                  </td>
                  <td>
                    <TechFormDateInput
                      name="underWateHullCleaningDateTwo"
                      value={form.underWateHullCleaningDateTwo}
                      error={formErrors.underWateHullCleaningDateTwo}
                      onChange={(value) => setForm((current) => ({ ...current, underWateHullCleaningDateTwo: value }))}
                      className={twMerge("rounded-md focus:outline-none", theme.uwhcAssistant.border)}
                    />
                  </td>
                  <td>{renderYesNo("underWaterHullCleaningHullInspectedTwo")}</td>
                  <td>{renderYesNo("underWaterHullCleaningHullCleanedTwo")}</td>
                  <td>{renderYesNo("underWateHullCleaningPropellerPolishedTwo")}</td>
                  <td>{renderYesNo("underWaterHullCleaningNzMpiStandardCleaningTwo")}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        <div data-testid="antifouling-system-overview" className="mt-2">
          <div className="min-w-0">
            <div className="grid max-w-[960px] grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col justify-start gap-1">
                <label className="flex items-center gap-1 text-sm font-bold">
                  Last dry-docking date or yard delivery date if newbuild
                  <span>
                    <InfoIcon
                      className="h-3 w-3"
                      data-tooltip-id="tooltip-lastDry"
                      data-tooltip-html="When hull cleaning/blasting/painting<br /> was carried out"
                    />
                  </span>
                  <Tooltip
                    id="tooltip-lastDry"
                    disableStyleInjection={true}
                    style={{
                      color: theme.sideBarComponent.iconAndText.tooltipText,
                      background: theme.sideBarComponent.background.tooltip,
                      borderRadius: "8px",
                      position: "absolute",
                      right: "0",
                    }}
                  />
                </label>
                <TechFormDateInput
                  name="lastDryDockingDate"
                  value={form.lastDryDockingDate}
                  error={formErrors.lastDryDockingDate}
                  onChange={(value) => setForm((current) => ({ ...current, lastDryDockingDate: value }))}
                  className={twMerge("w-full rounded-md focus:outline-none", theme.uwhcAssistant.border)}
                  wrapperClassName="w-full"
                />
              </div>

              <div className="flex flex-col justify-start gap-1">
                <label className="text-sm font-bold">Last dry-docking location/yard built</label>
                <input
                  type="text"
                  name="lastDryDockingLocation"
                  value={form.lastDryDockingLocation}
                  onChange={handleInputChange}
                  className={`h-[50px] w-full rounded-md border pl-2 text-sm focus:outline-none ${
                    formErrors.lastDryDockingLocation ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
              </div>
            </div>

            <img
              src={hullAreasGuide}
              alt="Hull coating areas showing top side, boot top, vertical bottom, and flat bottom"
              className="mx-auto mt-6 h-auto w-full max-w-[900px]"
            />

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div className="flex flex-col justify-start gap-1">
                <label className="text-sm font-medium" htmlFor="dateOfIssuanceAntifoulingCertificate">
                  Date of issuance Antifouling Certificate
                </label>
                <TechFormDateInput
                  name="dateOfIssuanceAntifoulingCertificate"
                  value={form.dateOfIssuanceAntifoulingCertificate}
                  error={formErrors.dateOfIssuanceAntifoulingCertificate}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, dateOfIssuanceAntifoulingCertificate: value }))
                  }
                  className={twMerge("rounded-md focus:outline-none", theme.uwhcAssistant.border)}
                />
              </div>

              <div className="flex flex-col justify-start gap-1">
                <label className="text-sm font-medium" htmlFor="dateOfPaintApplication">
                  Date of paint application
                </label>
                <TechFormDateInput
                  name="dateOfPaintApplication"
                  value={form.dateOfPaintApplication}
                  error={formErrors.dateOfPaintApplication}
                  onChange={(value) => setForm((current) => ({ ...current, dateOfPaintApplication: value }))}
                  className={twMerge("rounded-md focus:outline-none", theme.uwhcAssistant.border)}
                />
              </div>

              <div className="min-w-0">
                <label className="block text-sm font-medium" htmlFor="typeOfAntiFoulingSystem">
                  Type of Antifouling system
                </label>
                <Select
                  inputId="typeOfAntiFoulingSystem"
                  className="mt-1"
                  classNamePrefix="rs "
                  options={TYPE_OF_ANTIFOULING_OPTIONS}
                  value={form.typeOfAntiFoulingSystem}
                  onChange={handleChangeType}
                  placeholder="Select type"
                  styles={extraStyles}
                />
                <span className="block text-xs font-medium">As per certificate</span>
              </div>

              <div className="min-w-0">
                <label className="block text-sm font-medium" htmlFor="antiFoulingSystemManufacturer">
                  Antifouling system(s) manufacturer
                  <InfoIcon
                    className="ml-1 inline h-3 w-3"
                    data-tooltip-id="my-tooltip"
                    data-tooltip-html="In case of different systems for the vertical sides<br /> / flat bottom and boot top,<br /> please add this to the subsequent dropdowns"
                  />
                  <Tooltip
                    id="my-tooltip"
                    disableStyleInjection={true}
                    style={{
                      color: theme.sideBarComponent.iconAndText.tooltipText,
                      background: theme.sideBarComponent.background.tooltip,
                      borderRadius: "8px",
                      position: "absolute",
                      right: "0",
                    }}
                  />
                </label>
                <Select
                  className="mt-1"
                  classNamePrefix="rs"
                  data-test="tq4-antifouling-system-manufacturer-select"
                  options={manufacturerOptions}
                  inputId="antiFoulingSystemManufacturer"
                  value={manufacturerValue}
                  onChange={handleChangeManufacturer}
                  onMenuOpen={loadPaintOptions}
                  isLoading={isLoadingPaintOptions}
                  placeholder="Select Manufacturer"
                  styles={extraStyles}
                />
                <span className="block text-xs font-medium">As per certificate</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-[32px]">
          <AntifoulingCoatingField
            field="antiFoulingVerticalBottomCoating"
            label="Name of Anti Fouling coating on Vertical Bottom"
            value={form.antiFoulingVerticalBottomCoating}
            options={antiFoulingCoatingOptions}
            allowsCustomEntry={allowsCustomCoatingEntry}
            dataTest="tq4-antifouling-vertical-bottom-coating-select"
            error={formErrors.antiFoulingVerticalBottomCoating}
            inputBorderClassName={theme.uwhcAssistant.border}
            isLoading={isLoadingPaintOptions}
            selectStyles={extraStyles}
            onMenuOpen={loadPaintOptions}
            onValueChange={handleCoatingValueChange}
          />
          <AntifoulingCoatingField
            field="antiFoulingFlatBottomCoating"
            label="Name of Anti Fouling coating on Flat Bottom"
            value={form.antiFoulingFlatBottomCoating}
            options={antiFoulingCoatingOptions}
            allowsCustomEntry={allowsCustomCoatingEntry}
            dataTest="tq4-antifouling-flat-bottom-coating-select"
            error={formErrors.antiFoulingFlatBottomCoating}
            inputBorderClassName={theme.uwhcAssistant.border}
            isLoading={isLoadingPaintOptions}
            selectStyles={extraStyles}
            onMenuOpen={loadPaintOptions}
            onValueChange={handleCoatingValueChange}
          />
          <AntifoulingCoatingField
            field="antiFoulingBootTopCoating"
            label="Name of Anti Fouling coating on Boot Top"
            value={form.antiFoulingBootTopCoating}
            options={bootTopCoatingOptions}
            allowsCustomEntry={allowsCustomCoatingEntry}
            dataTest="tq4-antifouling-boot-top-coating-select"
            error={formErrors.antiFoulingBootTopCoating}
            inputBorderClassName={theme.uwhcAssistant.border}
            isLoading={isLoadingPaintOptions}
            selectStyles={extraStyles}
            onMenuOpen={loadPaintOptions}
            onValueChange={handleCoatingValueChange}
          />
          <p className="max-w-[220px] text-xs">{BOOT_TOP_GUIDANCE}</p>

          <div className="flex max-w-[220px] flex-col justify-start gap-1">
            <label className="text-sm font-bold">
              Service life of AFS
              <InfoIcon
                data-tooltip-id="service-life-afs-tooltip"
                className="ml-1 inline h-4 w-4"
                aria-label={SERVICE_LIFE_GUIDANCE}
              />
            </label>
            <Tooltip id="service-life-afs-tooltip" content={SERVICE_LIFE_GUIDANCE} />
            <input
              type="text"
              name="serviceLifeAfsMonths"
              value={form.serviceLifeAfsMonths}
              onChange={handleInputChange}
              className={`h-[50px] w-full rounded-md border pl-2 text-sm focus:outline-none ${
                formErrors.serviceLifeAfsMonths ? "border-red-600" : theme.uwhcAssistant.border
              }`}
            />
            <label className="block text-xs font-medium">Months</label>
          </div>

          <div className="flex max-w-[220px] flex-col justify-start gap-1">
            <label className="text-sm font-bold">Propeller painted/coated?</label>
            <div className="mt-1">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="propellerPaintedCoated"
                  value="yes"
                  checked={form.propellerPaintedCoated === "yes"}
                  onChange={handleInputChange}
                />
                <span className="ml-2">Yes</span>
              </label>
              <label className="ml-4 inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="propellerPaintedCoated"
                  value="no"
                  checked={form.propellerPaintedCoated === "no"}
                  onChange={handleInputChange}
                />
                <span className="ml-2">No</span>
              </label>
            </div>
          </div>

          <div className="flex flex-col justify-start gap-1">
            <label className="text-sm font-bold">Next scheduled dry docking</label>
            <TechFormDateInput
              name="nextScheduledDryDocking"
              value={form.nextScheduledDryDocking}
              error={formErrors.nextScheduledDryDocking}
              onChange={(value) => setForm((current) => ({ ...current, nextScheduledDryDocking: value }))}
              className={twMerge("rounded-md focus:outline-none", theme.uwhcAssistant.border)}
            />
          </div>

          <div className="flex max-w-[200px] flex-col justify-start gap-1">
            <label className="text-sm font-bold">
              Is there a procedure placed by owners for regular UWI/UWC?
              <InfoIcon
                className="ml-1 inline h-3 w-3"
                data-tooltip-id="tooltip-lastDryProc"
                data-tooltip-html="Is there a procedure put in place by owner for regular under-water inspection/cleaning/propeller polishig to ensure hull kept in efficient state?"
              />
              <Tooltip
                id="tooltip-lastDryProc"
                disableStyleInjection={true}
                style={{
                  color: theme.sideBarComponent.iconAndText.tooltipText,
                  background: theme.sideBarComponent.background.tooltip,
                  borderRadius: "8px",
                  position: "absolute",
                  right: "0",
                  maxWidth: "300px",
                }}
              />
            </label>

            <div className="mt-1">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="isThereAProcedurePlacedByOwners"
                  value="yes"
                  checked={form.isThereAProcedurePlacedByOwners === "yes"}
                  onChange={handleInputChange}
                />
                <span className="ml-2">Yes</span>
              </label>
              <label className="ml-4 inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="isThereAProcedurePlacedByOwners"
                  value="no"
                  checked={form.isThereAProcedurePlacedByOwners === "no"}
                  onChange={handleInputChange}
                />
                <span className="ml-2">No</span>
              </label>
            </div>
          </div>

          <div className="flex max-w-[200px] flex-col justify-start gap-1">
            <label className="text-sm font-bold">Frequency of scheduled inspections/cleanings</label>
            <input
              type="text"
              name="frequencyOfScheduledInspections"
              placeholder="Enter frequency.."
              value={form.frequencyOfScheduledInspections}
              onChange={handleInputChange}
              className={`h-[50px] w-full rounded-md border pl-2 text-sm focus:outline-none ${
                formErrors.frequencyOfScheduledInspections ? "border-red-600" : theme.uwhcAssistant.border
              }`}
            />
          </div>
        </div>
      </div>
    </LoadingWrapper>
  );
};
